import { Box, FormControlLabel, InputLabel, Typography } from '@mui/material';
import { DIFFERENT_COLOR, TYPE_ANALYTICS_MAP_VIEW } from 'common/defines/constants';
import PieChartCommon from 'components/Common/PieChartCommon';
import SkeletonCharts from 'components/Common/SkeletonCharts';
import { SwitchCustom } from 'components/Common/SwitchCustom';
import { useClientSettingsStyle } from 'components/Dashboard/ClientSettings/ClientSettingsStyle';
import { QUERY_KEY } from 'constants/constants';
import { get, isEmpty } from 'lodash';
import randomColor from 'randomcolor';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { getSizeVigorPaddyAnalytic, getVigorPaddyAnalytic } from 'services/analytics/apiAnalyticsConfig.services';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { changeSizeVigorPaddyList, mapViewSelector, setDataVigorPaddy } from 'store/slices/mapViewSlice';
import VigorPaddyCardLayer from '../../Layer/VigorPaddy';
import { SwipeableViewsCustom } from '../../SwipeableViewsCustom';
import InfoTable from '../CropCover/InfoTable';

const VigorPaddyAnalytics = () => {
  const dispatch = useAppDispatch();
  const [chartMode, setChartMode] = useState(false);
  const classes = useClientSettingsStyle();

  const { analyticId, sizeVigorPaddySelected, dataVigorPaddy, sizeVigorPaddyList } = useAppSelector(mapViewSelector);

  useQuery([QUERY_KEY.VIGOR_PADDY_ANALYTIC, analyticId], () => getSizeVigorPaddyAnalytic(analyticId || ''), {
    enabled: !!analyticId && isEmpty(sizeVigorPaddyList),
    onSuccess: (response) => {
      if (response.data) {
        dispatch(changeSizeVigorPaddyList(response.data));
      }
    },
  });

  const { data: dataCropType } = useQuery(
    [QUERY_KEY.SIZE_VIGOR_PADDY_ANALYTIC, analyticId, sizeVigorPaddySelected],
    () => getVigorPaddyAnalytic(analyticId || '', sizeVigorPaddySelected + ''),
    { enabled: !!analyticId && !!sizeVigorPaddySelected }
  );

  const dataPie = useMemo(() => {
    if (dataVigorPaddy) {
      return dataVigorPaddy.map((_item: any) => ({
        name: _item.type,
        value: _item.lduseArea,
        totalTree: _item.totalTree,
      }));
    }
    return [];
  }, [dataVigorPaddy]);

  const getDataChart = useMemo(() => {
    return dataVigorPaddy?.map((_item: any) => ({
      name: _item.type,
      value: chartMode ? _item.totalTree : _item.lduseArea,
    }));
  }, [chartMode, dataVigorPaddy]);

  const hasTreeCount = !!(dataVigorPaddy?.length && dataVigorPaddy.some((item: any) => item.totalTree > 0));

  const colorVigorPaddy = useMemo(() => {
    if (dataCropType?.data) {
      let colors: string[] = [];
      dispatch(
        setDataVigorPaddy(
          dataCropType.data.map((item: any, index: number) => {
            const colorRandom = randomColor({ luminosity: 'dark' });
            const _color = item.color || get(DIFFERENT_COLOR, `[${index}]`, colorRandom);
            colors.push(_color);
            return {
              ...item,
              visible: true,
              color: _color,
            };
          })
        )
      );
      return colors;
    }
    return [];
  }, [dataCropType, dispatch]);

  return (
    <SwipeableViewsCustom
      isDisableLabel
      analyticName={TYPE_ANALYTICS_MAP_VIEW.VIGOR_FIELD}
      analyticTab={
        <>
          {dataVigorPaddy?.length > 0 ? (
            <>
              <PieChartCommon title="Vigor Field" dataChart={getDataChart} colorCharts={colorVigorPaddy} />
              {hasTreeCount && (
                <>
                  <InputLabel sx={{ marginLeft: '25px', fontSize: '14px' }}>Chart Mode:</InputLabel>
                  <FormControlLabel
                    sx={{ marginLeft: '30px' }}
                    control={
                      <SwitchCustom
                        checked={chartMode}
                        onClick={() => {
                          setChartMode(!chartMode);
                        }}
                      />
                    }
                    label={<Typography className={classes.textSetting}>{chartMode ? 'Tree Count' : 'Area'}</Typography>}
                  />
                </>
              )}
              <Box component={'div'} sx={{ marginTop: '30px' }}>
                <InfoTable dataPie={dataPie} hasTreeCount={hasTreeCount} />
              </Box>
            </>
          ) : (
            <SkeletonCharts count={1} />
          )}
        </>
      }
      layerTab={<VigorPaddyCardLayer labelCard={TYPE_ANALYTICS_MAP_VIEW.VIGOR_FIELD} />}
      isShowDownloadButton={dataVigorPaddy?.length > 0}
    />
  );
};

export default VigorPaddyAnalytics;
